import React, { ComponentType, Suspense, lazy } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from 'components/V1/ErrorFallback/ErrorFallback';
import { PublicLayoutSpinner } from 'components/V1/Spinner/Spinner';
import PublicLayout from 'views/PublicLayout/PublicLayout';
import { useIsV2 } from 'components/V2/V2Switch';
import PublicLayoutV2 from 'views/PublicLayoutV2/PublicLayoutV2';
import Login from 'views/Login/Login';
import LoginV2 from 'views/Login/LoginV2';

const ResetPassword = lazy(() => import('views/ResetPassword/ResetPassword'));
const ResetPasswordV2 = lazy(
  () => import('views/ResetPassword/ResetPasswordV2')
);
const SetPassword = lazy(() => import('views/ResetPassword/SetPassword'));
const SetPasswordV2 = lazy(() => import('views/ResetPassword/SetPasswordV2'));
const Verify = lazy(() => import('views/Verification/Verify/Verify'));
const VerifyV2 = lazy(() => import('views/Verification/Verify/VerifyV2'));

interface PublicPages {
  Layout: ComponentType<{ children: React.ReactNode }>;
  Login: ComponentType;
  ResetPassword: ComponentType;
  SetPassword: ComponentType;
  Verify: ComponentType;
}

const v1Pages: PublicPages = {
  Layout: PublicLayout,
  Login,
  ResetPassword,
  SetPassword,
  Verify,
};

const v2Pages: PublicPages = {
  Layout: PublicLayoutV2,
  Login: LoginV2,
  ResetPassword: ResetPasswordV2,
  SetPassword: SetPasswordV2,
  Verify: VerifyV2,
};

function UnauthenticatedApp() {
  const location = useLocation();
  const pages = useIsV2() ? v2Pages : v1Pages;

  return (
    <pages.Layout>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Suspense fallback={<PublicLayoutSpinner />}>
          <Switch>
            <Route path="/" exact>
              <pages.Login />
            </Route>
            <Route path="/pre-live">
              <pages.Login />
            </Route>
            <Route path="/reset-password">
              <pages.ResetPassword />
            </Route>
            <Route path="/set-password">
              <pages.SetPassword />
            </Route>
            <Route path="/verification" exact>
              <pages.Verify />
            </Route>
            <Redirect
              to={{
                pathname: '/',
                state: { from: location },
              }}
            />
          </Switch>
        </Suspense>
      </ErrorBoundary>
    </pages.Layout>
  );
}

export default UnauthenticatedApp;
